/**
 * GESTOR DE ESCENA AR
 * Inicializa y configura la escena A-Frame y AR.js
 */

import { APP_STATES, AR_CONFIG, CAMERA_CONFIG, UI_CONFIG } from '@config/constants';
import { safeQuerySelector, executeWithDelay } from '@utils/domUtils';
import { StateManager } from './StateManager';
import { HitboxSystem } from './HitboxSystem';
import type {
  AFrameElement,
  SceneInfo,
  BrowserCompatibility,
  DOMElements
} from '@types';
import { pauseVideo, playVideoSafely } from '@/utils/videoUtils';

export class ARScene {
  private stateManager: StateManager;
  private hitboxSystem: HitboxSystem;
  private elements: DOMElements = {};
  private isInitialized: boolean = false;
  private initialVideoLastTime: number = 0;

  constructor(stateManager: StateManager, hitboxSystem: HitboxSystem) {
    this.stateManager = stateManager;
    this.hitboxSystem = hitboxSystem;

    this.initializeElements();
    this.setupEventListeners();
  }

  /**
   * Inicializa referencias a elementos de la escena
   */
  private initializeElements(): void {
    const selectors = {
      scene: 'a-scene',
      qrMarker: '#qrMarker',
      camera: '[camera]',
      initialButtons: '#initialButtons',
      videoInitial: '#videoInitial',
      homeState: '#homeState',
      initialState: '#initialState',
      facephiButton: '#facephiButton'
    };

    for (const [name, selector] of Object.entries(selectors)) {
      this.elements[name] = safeQuerySelector(selector);

      if (!this.elements[name]) {
        console.warn(`ARScene: Elemento no encontrado - ${name}`);
      }
    }
  }

  /**
   * Configura event listeners de la escena
   */
  private setupEventListeners(): void {
    // Listener para cuando A-Frame esté listo
    document.addEventListener('DOMContentLoaded', () => {
      executeWithDelay(() => {
        this.initializeAR();
      }, UI_CONFIG.INITIALIZATION_DELAY);
    });
    // HomeState e initialState para controlar su visibilidad cuando finalice el vídeo
    const homeStateContent = this.elements.homeState;
    const initialStateContent = this.elements.initialState;

    // Listener para el vídeo
    const video = this.elements.videoInitial as HTMLVideoElement;

    // Listener para clicks en la escena
    const scene = this.elements.scene;
    if (scene) {
      scene.addEventListener('click', this.handleSceneClick.bind(this));
      scene.addEventListener('touchend', this.handleSceneClick.bind(this));
    }

    // Listener para cambios de visibilidad del marcador
    const qrMarker = this.elements.qrMarker;
    if (qrMarker) {
      qrMarker.addEventListener('markerFound', () => {
        console.log('Marcador Hiro encontrado');
        if (initialStateContent && Boolean(initialStateContent.getAttribute('visible')) === true) {
          // Reanudar el video desde donde se quedó (o desde el inicio si es la primera vez)
          if (video) {
            try {
              if (!isNaN(this.initialVideoLastTime) && this.initialVideoLastTime > 0) {
                video.currentTime = this.initialVideoLastTime;
              } else {
                video.currentTime = 0;
              }
            } catch (e) {
              // ignorar si aún no está listo
            }
            playVideoSafely(video, 'videoInitial');
          }
        }
      });

      qrMarker.addEventListener('markerLost', () => {
        console.log('Marcador Hiro perdido');
        if (initialStateContent && Boolean(initialStateContent.getAttribute('visible')) === true) {
          // Guardar el tiempo actual y pausar cuando se pierde el marcador
          if (video) {
            try {
              this.initialVideoLastTime = video.currentTime || 0;
            } catch (e) {
              this.initialVideoLastTime = 0;
            }
            pauseVideo();
          }
        }
      });
    }
    // Al finalizar el vídeo de introducción, aparece HomeState
    if (video) {
      video.addEventListener('timeupdate', () => {
        const timeRemaining = 0.5; // medio segundo antes de que termine el vídeo y se muestre el fondo negro

        // Si el tiempo restante para que termine el vídeo es menor a 0.5 segundos, entonces muestra HomeState siempre y cuando esté en InitialState
        if (this.stateManager.getCurrentState() === APP_STATES.INITIAL && video.duration - video.currentTime <= timeRemaining) {
          console.log('Mostrando HomeState justo antes del final del vídeo');

          if (homeStateContent) {
            // Cambia el estado a HOME
            if (this.stateManager.getCurrentState() !== APP_STATES.HOME) {
              this.stateManager.changeState(APP_STATES.HOME);
            }
          } else {
            console.error("HomeState no encontrado");
          }
        }
      });

      video.addEventListener('ended', () => {
        // Se oculta el vídeo y se pausa cuando haya finalizado
        pauseVideo();
      });
    }

  }

  /**
   * Inicializa el sistema AR
   */
  private async initializeAR(): Promise<void> {
    try {
      console.log('Inicializando sistema AR...');

      // Verificar que A-Frame esté disponible
      if (typeof window.AFRAME === 'undefined') {
        throw new Error('A-Frame no está disponible');
      }

      // Configurar cámara AR
      this.setupARCamera();

      // Re-inicializar elementos después de que A-Frame cargue
      this.stateManager.reinitializeElements();

      this.isInitialized = true;
      console.log('Sistema AR inicializado correctamente');

    } catch (error) {
      console.error('Error inicializando AR:', error);
      throw error;
    }
  }

  /**
   * Configura la cámara AR
   */
  private setupARCamera(): void {
    const camera = this.elements.camera as AFrameElement;
    if (!camera) {
      console.warn('Elemento de cámara no encontrado');
      return;
    }

    // Aplicar configuración de cámara
    const cameraConfig = {
      ...CAMERA_CONFIG,
      'arjs-camera': AR_CONFIG
    };

    for (const [attribute, value] of Object.entries(cameraConfig)) {
      if (value !== null && value !== undefined) {
        camera.setAttribute(attribute, value);
      }
    }

    console.log('Cámara AR configurada');
  }

  /**
   * Maneja clicks en la escena
   */
  private handleSceneClick(event: Event): void {
    if (!this.isInitialized) {
      console.log('Sistema AR no inicializado, ignorando click');
      return;
    }

    const mouseEvent = event as MouseEvent;
    const touchEvent = event as TouchEvent;

    // Detectar qué botón fue clickeado
    const clickedButton = this.hitboxSystem.detectClickedButton(mouseEvent || touchEvent);

    if (clickedButton) {
      console.log(`Botón clickeado: ${clickedButton}`);

      // Delegar al StateManager
      const handled = this.stateManager.handleButtonClick(clickedButton);

      if (handled) {
        // Prevenir propagación si se manejó el click
        event.preventDefault();
        event.stopPropagation();
      }
    }
  }

  /**
   * Obtiene información de la escena
   */
  getSceneInfo(): SceneInfo {
    const qrMarker = this.elements.qrMarker as AFrameElement;
    const markerVisible = qrMarker ? qrMarker.object3D.visible : false;

    return {
      initialized: this.isInitialized,
      markerVisible,
      currentState: this.stateManager.getCurrentState(),
      elementsFound: Object.keys(this.elements).filter(key => this.elements[key] !== null)
    };
  }

  /**
   * Reinicia la escena
   */
  reset(): void {
    console.log('Reiniciando escena AR...');

    // Reiniciar estado
    this.stateManager.reset();

    console.log('Escena AR reiniciada');
  }

  /**
   * Verifica compatibilidad del navegador
   */
  checkBrowserCompatibility(): BrowserCompatibility {
    const compatibility: BrowserCompatibility = {
      webgl: this.checkWebGLSupport(),
      getUserMedia: this.checkGetUserMediaSupport(),
      deviceOrientation: this.checkDeviceOrientationSupport(),
      webrtc: this.checkWebRTCSupport(),
      fullscreen: this.checkFullscreenSupport(),
      touchEvents: 'ontouchstart' in window,
      userAgent: navigator.userAgent
    };

    console.log('Compatibilidad del navegador:', compatibility);
    return compatibility;
  }

  /**
   * Verifica soporte WebGL
   */
  private checkWebGLSupport(): boolean {
    try {
      const canvas = document.createElement('canvas');
      return !!(window.WebGLRenderingContext &&
        (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
    } catch (e) {
      return false;
    }
  }

  /**
   * Verifica soporte getUserMedia
   */
  private checkGetUserMediaSupport(): boolean {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  }

  /**
   * Verifica soporte Device Orientation
   */
  private checkDeviceOrientationSupport(): boolean {
    return 'DeviceOrientationEvent' in window;
  }

  /**
   * Verifica soporte WebRTC
   */
  private checkWebRTCSupport(): boolean {
    return !!(window.RTCPeerConnection || (window as any).webkitRTCPeerConnection || (window as any).mozRTCPeerConnection);
  }

  /**
   * Verifica soporte Fullscreen
   */
  private checkFullscreenSupport(): boolean {
    const element = document.documentElement as any;
    return !!(element.requestFullscreen ||
      element.mozRequestFullScreen ||
      element.webkitRequestFullscreen ||
      element.msRequestFullscreen);
  }

  /**
   * Limpia recursos de la escena
   */
  cleanup(): void {
    console.log('Limpiando recursos de ARScene...');

    // Detener actualizaciones
    if (this.hitboxSystem) {
      this.hitboxSystem.cleanup();
    }

    this.isInitialized = false;
  }
}